import {getTagTeamSuperstars} from "../models/tagTeam.js";
import {
    addSuperstarChampionships,
    getSuperstarChampionships,
    getSuperstarTagTeams,
    removeSuperstarChampionships
} from "../models/superstar.js";
import {
    addChampionshipMatchContracts,
    Championship,
    getChampionshipChampionshipReigns,
    getChampionshipCurrentReign,
    getChampionshipMatchContract,
    getChampionshipSuperstar
} from "../models/championship.js";
import {
    addTalentMatchContracts,
    getMatchContractEvent,
    MatchContract
} from "../models/matchContract.js";
import {
    getArrayMaxByProperty,
    getCurrentGame,
    getRosterMemberFaceOfTheCompany,
    getRosterMemberHot,
    getRosterMemberRank,
    getRosterMemberStale, getUniqueObjects,
    objectInArray, getRandomChoice, getRandomInt
} from "./helpers.js";
import {getChampionshipDisplayName, getChampionshipsDisplayName} from "../ui/ui-helpers.js";
import {
    addCompanyChampionships,
    addCompanyCompanyLogs,
    getCompanyChampionships,
    getCompanySegmentItems, removeCompanyChampionships
} from "../models/company.js";
import {
    addMatchChampionships,
    addMatchParticipants, getAllSuperstarsForMatch, getMatchChampionships,
    getMatchFeuds, getMatchGimmickMatch,
    getMatchParticipants,
    Match
} from "../models/match.js";
import {getSegmentSegmentItem} from "../models/segment.js";
import {
    changeProperty, getChampionshipCost,
    getChampionshipReignLength,
    getCompanyLatestEvent, getCompanyParentEvents,
    getCompanyRosterByType, getCompanyUnreadMessages, processChampionshipXpChange, processObjectiveCompleted
} from "./companies.js";
import {getChampionshipReignMatches} from "../models/championshipReign.js";
import {getGameCurrentUserCompany} from "./games.js";
import {
    Championships,
    CompanyLogs,
    db,
    loadDb,
    MatchContracts,
    Matches,
    Superstars,
    TagTeams
} from "../models/models.js";
import {addEventMatchContracts, addEventMatches} from "../models/event.js";
import {getRosterSuperstars} from "../models/roster.js";
import {getRosterMemberSuperstars, getSuperstarDisplayData} from "./superstars.js";
import {
    checkHangChampionships,
    getGimmickMatchForGraphic,
    getGimmickMatchGraphic,
    getMatchData, getMatchParticipantsGraphics,
    getMatchRefereeSuperstar
} from "./matches.js";
import {setManagersForMatchOrSegment} from "./managers.js";
import {globalUser} from "./globals.js";
import {CompanyLog} from "../models/companyLog.js";
import {getTagTeamDisplayData} from "./tagTeams.js";
import {championshipsGraphicsData} from "../data/graphics.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {getUpcomingMonthlyEventForWeeklyEvent} from "./events.js";
import {
    getChampionshipGraphicStrapColor,
    generateChampionshipName, getChampionshipGraphicGemsColor,
    getChampionshipGraphicPlateColor
} from "./generate.js";

function getUniqueChampionships(championships) {
    return championships.filter((value, index, self) =>
        index === self.findIndex((t) => (
            checkAreChampionshipsEqual(t, value)
        ))
    );
}

function checkAreChampionshipsEqual(a, b) {
    return (a.name === b.name && a.tagTeam === b.tagTeam && a.trio === b.trio && a.companyId === b.companyId)
}

function compareChampionships(a, b) {  // Previously compare_championships
    // Sort championships by rank, prestige then order created
    const ranks = {'Main Eventer': 4, 'Upper Midcarder': 3, 'Lower Midcarder': 2, 'Jabroni': 1, 'default': 1};
    const prestiges = {'S': 4, 'A': 3, 'B': 2, 'C': 1, 'default': 1};
    return a.retired - b.retired || (ranks[b.rank] || ranks['default']) - (ranks[a.rank] || ranks['default']) ||
        (prestiges[b.prestige] || prestiges['default']) - (prestiges[a.prestige] || prestiges['default']) ||
        b.xp - a.xp ||
        a.$loki - b.$loki;
}

function getChampionshipMatchData(championship) {  // Previously get_championship_match_data
    let data = {};
    data['name'] = championship.name;
    data['matchContract'] = championship.matchContract;
    data['championshipContract'] = championship.championshipContract;
    data['tagTeam'] = championship.tagTeam;
    data['trio'] = championship.trio;
    data['prestige'] = championship.prestige;
    return data;
}

async function getChampionshipBookingData(championship) {  // get_championship_roster_data
    let championshipData = JSON.parse(JSON.stringify(championship));
    championshipData['uid'] = championship.$loki;
    if (championship.matchContract) {
        const championshipMatchContract = await getChampionshipMatchContract(championship);
        if (championshipMatchContract) {
            championshipData['event'] = (await getMatchContractEvent(championshipMatchContract))?.name;
        }
    }
    return championshipData;
}

async function getMatchAvailableChampionships(participants, participantSuperstars, companyChampionships,
                                              gimmickMatch = null, event = null, skipBooked = false) {
    let data = {};

    // Get singles championships
    let allChampionships = [];
    if (participants[0].objectName === 'superstar') {
        allChampionships = await getMatchParticipantsSinglesChampionships(participants);
    }
    // Get tag championships
    else {
        allChampionships = await getMatchParticipantsTagChampionships(participants, companyChampionships)
    }

    // Sort championships
    allChampionships.sort(compareChampionships);

    // Check to skip booked championships
    if (skipBooked) {
        allChampionships = allChampionships.filter(x => !x.booked);
    }

    // Check for gimmick restrictions
    let availableChampionships = allChampionships.filter(x => checkChampionshipGimmickRestriction(x, gimmickMatch,
        event, participantSuperstars));

    // Get available championships
    data['availableChampionships'] = availableChampionships;

    // Add gimmick restricted championships
    data['gimmickRestrictedChampionships'] = [];
    let gimmickRestrictedChampionships = allChampionships.filter(x => !objectInArray(x, availableChampionships));
    for (const x of gimmickRestrictedChampionships) {
        data['gimmickRestrictedChampionships'].push({'name': getChampionshipDisplayName(x),
            'gimmickRestriction': x.gimmickRestriction})
    }

    return data;
}

async function getMatchParticipantsSinglesChampionships(participants) {
    let championships = [];
    for (const superstar of participants) {
        // Get superstar's championships
        championships = [...championships, ...await getSuperstarChampionships(superstar)];
    }
    return championships.filter(x => !x.tagTeam && !x.trio);
}

async function getMatchParticipantsTagChampionships(participants, companyChampionships) {
    let foundChampionships = [];

    // Check if trios match
    let trio = false;
    if (participants.length > 0) {
        trio = (await getTagTeamSuperstars(participants[0])).length === 3;
    }

    // Filter tag/trios championships
    if (trio) {
        companyChampionships = companyChampionships.filter(x => x.trio)
    }
    else {
        companyChampionships = companyChampionships.filter(x => x.tagTeam)
    }

    // Get all tag championships sets
    let tagChampionshipsSets = getTagChampionshipsSets(companyChampionships);

    for (const tagTeam of participants) {
        if (tagTeam.temporary && !tagTeam.trioTagTeam) {
            continue; // Temporary tag teams have no superstars, don't get their championships
        }

        // Get tag team superstars and their ids
        let tagTeamSuperstars = await getTagTeamSuperstars(tagTeam);
        if (tagTeam.trioTagTeam && tagTeamSuperstars.length > 0) {
            // Get trio superstars for trio tag team
            let teamTrio = (await getSuperstarTagTeams(tagTeamSuperstars[0])).find(x => x.trio);
            if (teamTrio) {
                tagTeamSuperstars = await getTagTeamSuperstars(teamTrio);
            }
        }
        let tagTeamSuperstarsIds = tagTeamSuperstars.map(x => x.$loki);

        // Get championship if the whole set is held by superstars of this team
        tagChampionshipsSets.forEach(championshipSet => {
            let championshipSetCheck = championshipSet.every(x => {
                return tagTeamSuperstarsIds.includes(x.superstarId)
            });
            if (championshipSetCheck) {
                // Get one championship from set
                foundChampionships.push(championshipSet[0]);
            }
        });
    }

    return foundChampionships;
}

function getTagChampionshipsSets(championships) {
    let tagChampionshipsSets = [];
    const uniqueChampionships = getUniqueChampionships(championships);
    uniqueChampionships.forEach(x => {
        let championshipsSet = championships.filter(y => checkAreChampionshipsEqual(y, x));
        tagChampionshipsSets.push(championshipsSet);
    });
    return tagChampionshipsSets;
}

function getVacatedChampionshipsForMatch(match, companyChampionships, trio) {
    let vacatedChampionships;
    if (match.type === 'singles') {
        vacatedChampionships = companyChampionships.filter(x => !x.tagTeam && !x.trio && x.vacated)
    }
    else {
        if (trio) {
            vacatedChampionships = companyChampionships.filter(x => x.trio && x.vacated)
        }
        else {
            vacatedChampionships = companyChampionships.filter(x => x.tagTeam && x.vacated)
        }
    }
    return getUniqueChampionships(vacatedChampionships);
}

async function getChampionshipHolder(championship) {  // get_championship_holder
    // Get championship's superstar or team of superstar
    let champion = null;
    const championshipSuperstar = await getChampionshipSuperstar(championship);

    if (championshipSuperstar) {
        if (championship.tagTeam || championship.trio) {
            // Find championshipSuperstar's team
            champion = (await getSuperstarTagTeams(championshipSuperstar)).find(x => {
                if (championship.tagTeam) {
                    return !x.temporary
                }
                else if (championship.trio) {
                    return !x.temporary && x.trio
                }
            });
        }
        else {
            champion = championshipSuperstar;
        }
    }

    return champion;
}

function checkChampionshipGimmickRestriction(championship, gimmickMatch, event, matchSuperstars) {  // check_championship_gimmick_restriction
    let gimmickRestriction = championship.gimmickRestriction;
    if (gimmickRestriction) {
        if (gimmickRestriction['type'] === 'gimmick_match') {
            if (!gimmickMatch) {
                return false;
            }
            if (gimmickRestriction['value'] && gimmickRestriction['value'] !== gimmickMatch.name) {
                return false
            }
        }
        if (gimmickRestriction['type'] === 'weekly_show') {
            if (!event.weeklyShow) {
                return false;
            }
        }
        if (['maximum_age', 'minimum_age'].includes(gimmickRestriction['type'])) {
            if (gimmickRestriction['type'] === 'maximum_age') {
                if (matchSuperstars.some(x => x.age > gimmickRestriction['value'])) {
                    return false
                }
            }
            if (gimmickRestriction['type'] === 'minimum_age') {
                if (matchSuperstars.some(x => x.age < gimmickRestriction['value'])) {
                    return false
                }
            }
        }
    }
    return true;
}

async function checkChampionshipSegmentItemsToUnlock(prestige, company) {  // check_championship_segment_items_to_unlock
    let unlocked = [];
    let unlocksDict = {'S': 'Championship Split', 'A': 'New Title Reveal', 'B': 'Option C'};
    let toUnlock = unlocksDict[prestige];
    if (toUnlock) {
        let segmentItemToUnlock = (await getCompanySegmentItems(company)).find(x => x.name === toUnlock);
        if (segmentItemToUnlock && !segmentItemToUnlock.unlocked) {
            segmentItemToUnlock.unlocked = true;
            unlocked.push(segmentItemToUnlock.name);
        }
    }
    return unlocked;
}

async function checkIsTopChampionship(c, company) {  // check_is_top_championship
    let topChampionship = (await getCompanyChampionships(company)).reduce((prev, current) => {
        return (prev.xp > current.xp) ? prev : current
    });
    return c.$loki === topChampionship.$loki;
}

async function getChampionshipXpChange({championship, company, month, year, mainEvent = false,
                                        championshipDefendedOrRetained = false, championshipWon = false, match = null,
                                        segment = null, weeklyShow = false} = {}) {  // get_championship_xp_change
    let data = {};

    if (match) {
        // Get rating change
        data['rating_change'] = getChampionshipXpChangeForMatchRating(match.rating, weeklyShow);

        // Get feuds change
        let feuds = await getMatchFeuds(match);
        data['feuds_change'] = getChampionshipXpChangeForFeuds(feuds, weeklyShow);

        // Get reign change
        data['reign_change'] = await getChampionshipXpChangeForReign(championship, month, year, championshipDefendedOrRetained,
            weeklyShow);

        // Get champion/challenger change
        let matchParticipants = await getMatchParticipants(match);
        let championshipHolder = await getChampionshipHolder(championship);
        let champion = championshipHolder ? matchParticipants.find(x => championshipHolder.$loki === x.$loki) : null;
        if (champion) {
            data['champion_change'] = await getChampionshipXpChangeForChampion(championship, champion, championshipWon, weeklyShow);
            // Get challenger change if championship not won
            if (!championshipWon) {
                let challengers = matchParticipants.filter(x => x.$loki !== champion.$loki);
                data['challenger_change'] = await getChampionshipXpChangeForChallengers(championship, challengers, weeklyShow);
            }
        }
    }

    // Get segment change
    if (segment) {
        const segmentItem = await getSegmentSegmentItem(segment);
        data['segment_change'] = getChampionshipXpChangeForSegmentItem(segmentItem, segment.rating);
    }

    // Get main event change
    let isTopChampionship = await checkIsTopChampionship(championship, company);
    data['main_event_change'] = getChampionshipXpChangeForMainEvent(mainEvent, isTopChampionship, weeklyShow, segment);

    // Get total xp change
    data['totalXpChange'] = Object.values(data).reduce((a, b) => a + b);

    return data;
}

function getChampionshipXpChangeForMainEvent(mainEvent, isTopChampionship, weeklyShow = false, segment = null) {  // get_championship_xp_change_for_main_event
    let xpChange = 0;
    if (mainEvent && !isTopChampionship) {
        xpChange += 4;
    }
    if (!mainEvent && isTopChampionship && !segment) {
        xpChange -= 6;
    }
    if (weeklyShow) {
        xpChange = Math.round(xpChange / 2);
    }
    return xpChange;
}

function getChampionshipXpChangeForMatchRating(matchRating, weeklyShow = false) {  // get_championship_xp_change_for_match_rating
    let xpChange = 0;
    if (matchRating !== null) {
        const ratings = {
            1.0: -15, 1.25: -14, 1.5: -12, 1.75: -10,
            2.0: -8, 2.25: -6, 2.5: -4, 2.75: -3,
            3.0: -2, 3.25: 0, 3.5: 1, 3.75: 2,
            4.0: 3, 4.25: 4, 4.5: 5, 4.75: 5,
            5.0: 6
        };
        if (ratings.hasOwnProperty(matchRating)) {
            xpChange = ratings[matchRating];
        }
        if (matchRating > 5) {
            xpChange = Math.max(...Object.values(ratings));
        }
        if (matchRating < 1) {
            xpChange = Math.min(...Object.values(ratings));
        }
        if (weeklyShow) {
            xpChange = Math.round(xpChange / 2);
        }
    }
    return xpChange;
}

function getChampionshipXpChangeForSegmentItem(segmentItem, segmentRating) {  // get_championship_xp_change_for_segment_item
    // Get xp change if segment is linked to a title
    let xpChange = 0;
    const ratings = {
        'Demand Title Shot': 3, 'Cash-In Tease': 3, 'Champion Package': 5, 'Champion Coronation': 6,
        'Lost My Smile': -15, 'Title Destruction': -15, 'Title Disrespect': -5, 'This Belongs To You': -10
    };

    // Get xp change for segment item
    if (ratings.hasOwnProperty(segmentItem.name)) {
        let segmentXpChange = ratings[segmentItem.name];

        if (segmentRating !== null) {
            // If positive boost, increase change for high rating, decrease for low rating
            if (segmentXpChange > 0) {
                if (segmentRating >= 4.5) {
                    segmentXpChange *= 1.5;
                } else if (segmentRating <= 2.5) {
                    segmentXpChange /= 1.5;
                }
            // If negative boost, decrease change for high rating, increase for low rating
            } else {
                if (segmentRating >= 4.5) {
                    segmentXpChange /= 1.5;
                } else if (segmentRating <= 2.5) {
                    segmentXpChange *= 1.5;
                }
            }
        }

        xpChange += Math.floor(segmentXpChange);
    }

    return xpChange;
}


function getChampionshipXpChangeForFeuds(feuds, weeklyShow = false) {  // get_championship_xp_change_for_feuds
    // Raise prestige for match feuds if any feuds are hot

    let xpChange = 0;
    for (const feud of feuds) {
        if (feud.hot) {
            xpChange += 4;
        }
        if (feud.stale) {
            xpChange -= 8;
        }
    }

    if (weeklyShow) {
        xpChange = Math.round(xpChange / 2);
    }

    if (xpChange > 8) {
        xpChange = 8;
    }
    if (xpChange < -16) {
        xpChange = -16;
    }

    return xpChange;
}

async function getChampionshipXpChangeForReign(championship, month, year, championshipDefendedOrRetained = false,
                                               weeklyShow = false) {  // get_championship_xp_change_for_reign
    let xpChange = 0;
    const currentReign = await getChampionshipCurrentReign(championship);

    if (currentReign) {
        if (championshipDefendedOrRetained && !championship.vacated) {  // For vacant title that didn't change hands
            const reignLength = getChampionshipReignLength(currentReign, month, year);
            const numberOfDefenses = (await getChampionshipReignMatches(currentReign)).length;
            xpChange += getChampionshipXpChangeForReignDefense(reignLength, numberOfDefenses, weeklyShow);
        } else {
            xpChange += await getChampionshipXpChangeForReignEnd(currentReign, championship, month, year);
        }
    }
    return xpChange;
}

async function getChampionshipXpChangeForReignEnd(reign, championship, month, year, xpChange = 0, i = 1) {  // get_championship_xp_change_for_reign_end
    // Get previous reign that was not vacated
    const previousReign = await getPreviousChampionshipReign(reign, championship);
    if (previousReign && !previousReign.vacated && i < 5) {  // Go through a max of last 5 reigns (-30 XP Max)

        // If reign shorter than 3 months, decrease xpChange
        const reignLength = getChampionshipReignLength(previousReign, month, year);
        if (reignLength < 3) {
            // Raise decrease for every short reign
            xpChange -= 6 * i;

            // Go through previous reign
            i += 1;
            return getChampionshipXpChangeForReignEnd(previousReign, championship, month, year, xpChange, i);
        }
    }

    // Return xpChange when previous reign not found or not short
    return xpChange;
}

async function getPreviousChampionshipReign(reign, championship) {  // get_previous_championship_reign
    const allReigns = (await getChampionshipChampionshipReigns(championship)).reverse();
    let reignIndex = allReigns.findIndex(x => x.$loki === reign.$loki);
    return allReigns[reignIndex + 1];
}

function getChampionshipXpChangeForReignDefense(reignLength, numberOfDefenses, weeklyShow = false) {  // get_championship_xp_change_for_reign_defense
    // Raise prestige for every 3 months of reign
    let xpChange = 0;

    if (!weeklyShow) {
        if (reignLength && reignLength % 3 === 0) {

            // Increase xp by number of defenses
            xpChange += numberOfDefenses;

            // If defenses are over 30% of reign months, add reign months boost
            if (numberOfDefenses > (reignLength / 3)) {
                xpChange += Math.floor(reignLength / 2);
            }

            // Maximum change
            if (xpChange > 30) {
                xpChange = 30;
            }
        }
    }

    return xpChange;
}

async function getChampionshipXpChangeForChallengers(championship, challengers, weeklyShow = false) {  // get_championship_xp_change_for_challengers
    let xpChange = 0;
    const ranks = {'Main Eventer': 4, 'Upper Midcarder': 3, 'Lower Midcarder': 2, 'Jabroni': 1};
    const prestigeRanks = {'S': 4, 'A': 4, 'B': 3, 'C': 2};

    // Get rank values for championship and challengers
    const championshipRankValue = prestigeRanks[championship.prestige] || 1;

    for (const challenger of challengers) {
        let rank = await getRosterMemberRank(challenger);
        const challengerRankValue = ranks[rank] || 1;
        const rankDifference = challengerRankValue - championshipRankValue;

        // Decrease prestige if challenger rank is lower than championship rank
        if (rankDifference < 0) {
            xpChange += rankDifference * 5;
        }
        // Increase prestige if challenger rank is higher than championship rank
        else if (rankDifference > 0) {
            xpChange += rankDifference * 3;
        }

        // Increase prestige if face of the company
        if (await getRosterMemberFaceOfTheCompany(challenger)) {
            xpChange += 2;
        }

        // Increase prestige if hot
        if (await getRosterMemberHot(challenger)) {
            xpChange += 3;
        }

        // Decrease prestige if stale
        if (await getRosterMemberStale(challenger)) {
            xpChange -= 4;
        }
    }

    if (challengers.length > 0) {
        xpChange = Math.floor(xpChange / challengers.length);
    }

    if (weeklyShow) {
        xpChange = Math.round(xpChange / 2);
    }

    // Min/max values
    if (xpChange > 20) {
        xpChange = 20;
    }
    if (xpChange < -20) {
        xpChange = -20;
    }

    return xpChange;
}

async function getChampionshipXpChangeForChampion(championship, champion, championshipWon = false, weeklyShow = false) {  // get_championship_xp_change_for_champion
    let xpChange = 0;
    const ranks = {'Main Eventer': 4, 'Upper Midcarder': 3, 'Lower Midcarder': 2, 'Jabroni': 1};
    const prestigeRanks = {'S': 4, 'A': 4, 'B': 3, 'C': 2};
    const changes = {
        'positive_rank_difference_multiplier': 2,
        'negative_rank_difference_multiplier': 4,
        'fotc_change': 2,
        'hot_change': 3,
        'stale_change': -4,
    };
    // Higher change for championship win
    if (championshipWon) {
        changes['positive_rank_difference_multiplier'] = 4;
        changes['negative_rank_difference_multiplier'] = 8;
        changes['fotc_change'] = 4;
        changes['hot_change'] = 6;
        changes['stale_change'] = -8;
    }

    // Get rank values for championship and champion
    const championshipRankValue = prestigeRanks[championship.prestige] || 1;
    const rank = await getRosterMemberRank(champion);
    const championRankValue = ranks[rank] || 1;
    const rankDifference = championRankValue - championshipRankValue;

    // Decrease prestige if champion rank is lower than championship rank
    if (rankDifference < 0) {
        xpChange += rankDifference * changes['negative_rank_difference_multiplier'];
    }
    // Increase prestige if champion rank is higher than championship rank
    else if (rankDifference > 0) {
        xpChange += rankDifference * changes['positive_rank_difference_multiplier'];
    }

    if (await getRosterMemberFaceOfTheCompany(champion)) {
        xpChange += changes['fotc_change'];
    }

    if (await getRosterMemberHot(champion)) {
        xpChange += changes['hot_change'];
    }

    if (await getRosterMemberStale(champion)) {
        xpChange += changes['stale_change'];
    }

    // Reduce champion passive boost on weekly shows
    if (weeklyShow && !championshipWon) {
        xpChange = Math.round(xpChange / 2);
    }

    return xpChange;
}

async function getChampionshipContractHolders({teamType}) {  // get_championship_contract_holders
    let holders = [];
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const companyChampionships = await getCompanyChampionships(userCompany);

    let championshipContracts = companyChampionships.filter(x => x.championshipContract);
    if (!['tag-team', 'trio'].includes(teamType)) {
        championshipContracts = championshipContracts.filter(x => !x.tagTeam && !x.trio);
    } else {
        if (teamType === 'trio') {
            championshipContracts = championshipContracts.filter(x => x.trio);
        } else {
            championshipContracts = championshipContracts.filter(x => x.tagTeam);
        }
    }
    for (const c of championshipContracts) {
        if (!c.vacated) {
            let holder = await getChampionshipHolder(c);
            if (holder) {
                holders.push(holder)
            }
        }
    }

    holders = getUniqueObjects(holders);

    // Get data to return
    return holders.map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });
}

async function getChampionshipsForCashin({uid} = {}) {  // get_championships_for_cashin
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const companyChampionships = await getCompanyChampionships(userCompany);

    const championshipContract = await Championships.get(uid);
    const allChampionships = companyChampionships.filter(x => !x.championshipContract && !x.vacated);
    allChampionships.sort(compareChampionships);

    let championships = allChampionships.filter(x => x.tagTeam === championshipContract.tagTeam &&
        x.trio === championshipContract.trio);
    championships = getUniqueChampionships(championships);

    // Get data to return
    return championships.map(x => {
        return {'name': x.name, 'uid': x.$loki}
    });
}

async function addCashinFutureMatch({championshipContractUid, championshipUid}) {
    const data = {};
    try {
        const currentGame = await getCurrentGame();
        const userCompany = await getGameCurrentUserCompany(currentGame);
        const companyChampionships = await getCompanyChampionships(userCompany);
        const latestEvent = await getCompanyLatestEvent(userCompany);
        const mainRoster = await getCompanyRosterByType(userCompany, 'main');
        const rosterSuperstars = await getRosterSuperstars(mainRoster);

        // Get championship contract and challenger
        const championshipContract = await Championships.get(championshipContractUid);
        const challenger = await getChampionshipHolder(championshipContract);

        // Get championship and champion
        const championship = await Championships.get(championshipUid);
        if (!championship) {
            data['error'] = 'championship-not-found';
            return data;
        }
        const champion = await getChampionshipHolder(championship);

        // Check for production packages only
        if (latestEvent.activeEffect === 'production_packages_only') {
            data['error'] = 'production-packages-only';
            return data;
        }

        // Check if champion cashing in on themselves
        if (challenger.$loki === champion.$loki) {
            data['error'] = 'challenger-is-champion';
            return data;
        }

        // Create match
        let matchParticipants = [challenger, champion];
        let matchType = challenger.objectName === 'superstar' ? 'singles' : 'tag';
        const newMatch = Match(latestEvent.matchesCount, matchType);
        await Matches.insert(newMatch);
        await addEventMatches(latestEvent, [newMatch]);
        latestEvent.matchesCount += 1;
        await addMatchParticipants(newMatch, matchParticipants);
        await addMatchChampionships(newMatch, [championship]);
        for (const c of companyChampionships.filter(x => checkAreChampionshipsEqual(x, championship))) {
            c.booked = true;
        }

        // Check superstars
        let matchSuperstars = await getAllSuperstarsForMatch(newMatch);
        for (let s of matchSuperstars) {
            if (s.currentAttire > 0) {
                s.currentAttire = 0;
            }
            if (latestEvent.activeEffect === 'no_women_bookable' && s.gender === 'female') {
                data['error'] = 'no-women-allowed';
                return data;
            } else if (latestEvent.activeEffect === 'no_men_bookable' && s.gender === 'male') {
                data['error'] = 'no-men-allowed';
                return data;
            }
        }

        // Add managers
        if (globalUser.paid) {
            setManagersForMatchOrSegment(newMatch, matchSuperstars, rosterSuperstars);
        }

        // Remove championship contract
        for (let s of (await getRosterMemberSuperstars(challenger))) {
            const c = (await getSuperstarChampionships(s)).find(x => x.championshipContract &&
                x.name === championshipContract.name);
            if (c) {
                await removeSuperstarChampionships(s, [c]);
                await removeCompanyChampionships(userCompany, [c]);
                await Championships.remove(c);
            }
        }

        db.saveDatabase();

        // Get data to return
        data['match'] = await getMatchData({
            match: newMatch, companyChampionships: companyChampionships,
            rosterSuperstars: rosterSuperstars, play: true
        });
        data['challenger'] = challenger.name;
        data['championshipContract'] = championshipContract.name;
        data['points'] = userCompany.points;
    }
    finally {
        if (data['error']) {
            await loadDb(db.filename);
        }
    }

    return data;
}

async function addCashinToMatch({challengerUid, cashinTime, matchUid}) {  // add_cashin_to_match
    const data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const latestEvent = await getCompanyLatestEvent(userCompany);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    data['objectivesCompleted'] = [];
    let cashinSinglesAvailable = false;
    let cashinTagAvailable = false;
    let cashinTriosAvailable = false;
    let championshipContract;
    let matchSuperstars;

    // Get match and challenger
    const match = await Matches.get(matchUid);
    let challenger;
    if (match.type === 'singles') {
        challenger = await Superstars.get(challengerUid)
    }
    else {
        challenger = await TagTeams.get(challengerUid)
    }
    if (!challenger) {
        data['error'] = 'challenger-not-found';
        return data;
    }

    // Get match participants
    const matchParticipants = await getMatchParticipants(match);
    const matchParticipantNames = matchParticipants.map(x => x.name).join(' vs ');

    // Check superstars
    let challengerSuperstars = await getRosterMemberSuperstars(challenger);
    for (let s of challengerSuperstars) {
        if (s.currentAttire > 0) {
            s.currentAttire = 0;
        }
        if (latestEvent.activeEffect === 'no_women_bookable' && s.gender === 'female') {
            data['error'] = 'no-women-allowed';
            return data;
        } else if (latestEvent.activeEffect === 'no_men_bookable' && s.gender === 'male') {
            data['error'] = 'no-men-allowed';
            return data;
        }
    }

    // Check if challenger in match
    if (objectInArray(challenger, matchParticipants)) {
        data['error'] = 'challenger-in-match';
        return data;
    }

    // Find championship contract
    for (const c of (await getSuperstarChampionships(challengerSuperstars[0]))) {
        if (c.championshipContract) {
            championshipContract = c;
            break;
        }
    }

    // Add challenger to current match
    if (cashinTime === '0') {
        await addMatchParticipants(match, [challenger]);
        matchParticipants.push(challenger);

        // Remove championship contract
        for (const s of challengerSuperstars) {
            const c = (await getSuperstarChampionships(s)).find(x => x.championshipContract &&
                x.name === championshipContract.name);
            if (c) {
                await removeSuperstarChampionships(s, [c]);
                await removeCompanyChampionships(userCompany, [c]);
                await Championships.remove(c);
            }
        }

        // Check for championship contracts
        for (let championship of (await getCompanyChampionships(userCompany))) {
            if (championship.championshipContract && !championship.vacated) {
                if (!championship.tagTeam && !championship.trio) {
                    cashinSinglesAvailable = true;
                } else {
                    if (championship.trio) {
                        cashinTriosAvailable = true;
                    } else {
                        cashinTagAvailable = true;
                    }
                }
            }
        }

        // Add managers
        matchSuperstars = await getAllSuperstarsForMatch(match);
        if (globalUser.paid) {
            setManagersForMatchOrSegment(match, matchSuperstars, rosterSuperstars);
        }

        // Holy shit moment award
        let pointsGained = 30;
        userCompany.points += pointsGained;

        // Change objective property
        const objectiveCompleted = await changeProperty({name: 'holy shit moments', change: 1, company: userCompany});
        if (objectiveCompleted) {
            await processObjectiveCompleted(objectiveCompleted, userCompany);
            const unreadMessages = await getCompanyUnreadMessages(userCompany);
            data['unreadMessages'] = unreadMessages.length;
        }

        // Add company log
        const newCompanyLog = CompanyLog(currentGame.monthCount, userCompany.realYear);
        CompanyLogs.insert(newCompanyLog);
        newCompanyLog.text = `${challenger.name} cashed in ${championshipContract.name} during ${matchParticipantNames}!`;
        newCompanyLog.type = 'moment';
        newCompanyLog.realYear = currentGame.year;
        newCompanyLog.eventName = latestEvent.name;
        newCompanyLog.value = 6;
        await addCompanyCompanyLogs(userCompany, [newCompanyLog]);
    }

    db.saveDatabase();

    // Get graphics if challenger added to current match
    if (cashinTime === '0') {
        // Get match graphics
        let allChampionships = (await Promise.all(matchSuperstars.map(x => getSuperstarChampionships(x)))).flat();
        let refereeSuperstar = await getMatchRefereeSuperstar(match, rosterSuperstars);

        // Get gimmick match graphic
        let gimmickMatchForGraphic = await getGimmickMatchForGraphic((await getMatchGimmickMatch(match)), match);
        let gimmickMatchGraphic = getGimmickMatchGraphic(gimmickMatchForGraphic);

        let hangChampionships = checkHangChampionships(gimmickMatchGraphic);
        let championshipsToRemove = hangChampionships ? [...(await getMatchChampionships(match))] : [];
        data['graphics'] = await getMatchParticipantsGraphics(match, matchParticipants, allChampionships, matchSuperstars,
            rosterSuperstars, refereeSuperstar, gimmickMatchForGraphic, championshipsToRemove);
    }

    // Get data to return
    data['championshipContract'] = championshipContract.name;
    data['cashinSinglesAvailable'] = cashinSinglesAvailable;
    data['cashinTagAvailable'] = cashinTagAvailable;
    data['cashinTriosAvailable'] = cashinTriosAvailable;
    if (match.type === 'singles') {
        data['challenger'] = await getSuperstarDisplayData({superstar: challenger, managersUsed: match.managersUsed,
            game: currentGame, rosterSuperstars});
    } else {
        data['challenger'] = await getTagTeamDisplayData({tagTeam: challenger, managersUsed: match.managersUsed,
            rosterSuperstars});
    }
    data['points'] = userCompany.points;
    data['fans'] = userCompany.fans;

    return data;
}

async function unifyChampionships(championships, company, companyChampionships, game, newChampionshipName = null) {  // unify_championships
    let unificationXpChange = 0;
    const unifyPrestigeXpData = {'S': 150, 'A': 80, 'B': 40, 'C': 10};

    // Get championship to unify with (highest xp)
    let championshipToUnifyWith = getArrayMaxByProperty(championships, 'xp');
    if (company.storyName === 'passion') {
        // Keep POW championship
        const powChampionship = championships.find(c => c.name === 'POW');
        if (powChampionship) {
            championshipToUnifyWith = powChampionship
        }
    }

    // Get championships to unify
    const championshipsToUnify = championships.filter(c => c.$loki !== championshipToUnifyWith.$loki);

    // Unify championships
    for (const championship of championshipsToUnify) {
        let championshipsSet = companyChampionships.filter(c => checkAreChampionshipsEqual(c, championship));

        // Process championships
        for (const x of championshipsSet) {

            // Remove championship from champion
            const champion = await getChampionshipSuperstar(x);
            if (champion) {
                await removeSuperstarChampionships(champion, [x]);

                // End current reign
                const oldReign = await getChampionshipCurrentReign(x);
                if (oldReign) {
                    oldReign.endMonth = game.monthCount;
                    oldReign.endYear = game.year;
                    oldReign.retired = true;
                }
            }

            if (x.matchContract || x.championshipContract) {
                await removeCompanyChampionships(company, [x]);
            }
            x.retired = true;
        }

        // Get championship prestige to add
        unificationXpChange += unifyPrestigeXpData[championship.prestige] || 10;
    }

    // Get data
    let data = {
        'unifiedChampionships': getChampionshipsDisplayName(championshipsToUnify),
        'unifiedChampionshipWith': getChampionshipDisplayName(championshipToUnifyWith),
        'multipleChampionships': championshipToUnifyWith.trio || championshipToUnifyWith.tagTeam || championships.length > 2
    };

    // Update name (if not POW title)
    if (newChampionshipName && !(company.storyName === 'passion' && championshipToUnifyWith.name === 'POW')) {
        const championshipToUnifyWithSet = companyChampionships
            .filter(c => checkAreChampionshipsEqual(c, championshipToUnifyWith));
        for (const c of championshipToUnifyWithSet) {
            c.name = newChampionshipName
        }
        data['newChampionshipName'] = getChampionshipDisplayName(championshipToUnifyWith)
    }

    // Increase prestige of championship to unify with
    if (globalUser.paid) {
        const championshipPrestigeData = await processChampionshipXpChange(championshipToUnifyWith, unificationXpChange,
            companyChampionships);
        data['xpChange'] = unificationXpChange;

        // Check for objective completed
        if (championshipPrestigeData['prestigeIncrease']) {
            const objectiveCompleted = await changeProperty({name: 'championship prestige', change: 1,
                company: company, championship: championshipToUnifyWith});
            if (objectiveCompleted) {
                await processObjectiveCompleted(objectiveCompleted, company);
            }

            // If prestige increased over highest value, unlock prestige bonus segments
            if (championshipPrestigeData['firstPrestigeIncrease']) {
                await checkChampionshipSegmentItemsToUnlock(championshipToUnifyWith.prestige, company)
            }
        }
    }

    return data
}

async function runCustomizeChampionshipAction(championship, companyChampionships) {  // run_customize_championship_action
    const holder = await getChampionshipSuperstar(championship);
    const partsChanged = [];

    if (!holder || !championship.graphic) {
        return null;
    }

    // Get championship graphic
    const graphic = JSON.parse(JSON.stringify(championship.graphic));

    // Change sideplate design
    if (getRandomInt(0, 100) <= 30) {
        const sidePlatesChoices = championshipsGraphicsData['sidePlates'];
        const sidePlatesObject = getRandomChoice(sidePlatesChoices);
        graphic['sidePlates']['object'] = sidePlatesObject['number'];
        graphic['sideGems']['object'] = sidePlatesObject['sideGemsPart'];
        partsChanged.push('side_plates');
    }

    // Change center and side plate colors
    if (getRandomInt(0, 100) <= 45) {
        graphic['centerPlate']['colors'] = [getChampionshipGraphicPlateColor(true, 50)];
        let sidePlatesColor = graphic['centerPlate']['colors'];
        if (getRandomInt(0, 100) <= 30) {
            sidePlatesColor = [getChampionshipGraphicPlateColor(true, 50)];
        }
        graphic['sidePlates']['colors'] = sidePlatesColor;
        partsChanged.push('plate_colors');
    }

    // Change strap color
    if (getRandomInt(0, 100) <= 60 || !partsChanged.length) {
        graphic['strap']['colors'] = [getChampionshipGraphicStrapColor(true, 50)];
        partsChanged.push('strap_colors');
    }

    // Change center and side gems colors
    if (getRandomInt(0, 100) <= 30) {
        graphic['centerGems']['colors'] = [getChampionshipGraphicGemsColor()];
        graphic['sideGems']['colors'] = graphic['centerGems']['colors'];
        partsChanged.push('gem_colors');
    }

    // Set championship(s) graphic to new graphic
    const championshipsSet = companyChampionships.filter(c => checkAreChampionshipsEqual(c, championship));
    for (const c of championshipsSet) {
        c.graphic = graphic;
    }

    return championship.graphic;
}

async function runSplitChampionshipAction(championship, companyChampionships, company, newChampionshipName = null) {  // run_split_championship_action
    // Divide Obsidian championship into 2 Gold championships

    const prestigeRanks = {'S': 4, 'A': 3, 'B': 2, 'C': 1};
    if (prestigeRanks[championship.prestige] < 4) {
        return null;
    }

    // Reduce prestige and xp of championship(s)
    const championshipsSet = companyChampionships.filter(c => checkAreChampionshipsEqual(c, championship));
    for (const c of championshipsSet) {
        c.prestige = 'A';
        c.highestPrestige = c.prestige;
        c.xp = 300;
    }

    // Get number of duplicate championships to create
    let numberOfDuplicateChampionships = 1;
    let teamType = null;
    if (championship.tagTeam) {
        numberOfDuplicateChampionships = 2;
        teamType = 'tag-team';
    } else if (championship.trio) {
        numberOfDuplicateChampionships = 3;
        teamType = 'trio';
    }

    // Generate strap color
    let graphic = null;
    if (championship.graphic) {
        graphic = JSON.parse(JSON.stringify(championship.graphic));
        // Generate strap color once for all new championships
        let strapColor = [getChampionshipGraphicStrapColor(true)];
        while (strapColor[0] === graphic.strap.colors[0]) {
            strapColor = [getChampionshipGraphicStrapColor(true)];
        }
        graphic['strap']['colors'] = strapColor;
    }

    // Get championship's name
    let name;
    if (newChampionshipName) {
        name = newChampionshipName;
    } else {
        // Generate unique name
        const womens = championship.gender === 'female' || championship.name.includes('Women');
        name = generateChampionshipName(company.name, championship.rank, teamType, womens);
        while (companyChampionships.some(x => x.name === name)) {
            name = generateChampionshipName(company.name, championship.rank, teamType, womens);
        }
    }

    // Create duplicate(s) of championship
    const newChampionships = [];
    for (let i = 0; i < numberOfDuplicateChampionships; i++) {
        const newChampionship = Championship(name);
        await Championships.insert(newChampionship);
        newChampionship.tagTeam = championship.tagTeam;
        newChampionship.trio = championship.trio;
        newChampionship.gender = championship.gender;
        newChampionship.prestige = championship.prestige;
        newChampionship.highestPrestige = newChampionship.prestige;
        newChampionship.xp = championship.xp;
        newChampionship.vacated = true;
        // Change strap color
        if (championship.graphic) {
            newChampionship.graphic = graphic;
        }
        newChampionships.push(newChampionship);
    }
    await addCompanyChampionships(company, newChampionships);

    return newChampionships;
}

async function runOptionCAction(championship, companyChampionships, company, month, year) {  // run_option_c_action
    // Vacate championship, create match contract at next monthly event. For Silver Championship Only.

    const holder = await getChampionshipHolder(championship);
    const holderSuperstars = await getRosterMemberSuperstars(holder);
    const currentEvent = await getCompanyLatestEvent(company);
    const parentEvents = await getCompanyParentEvents(company);
    const prestigeRanks = {'S': 4, 'A': 3, 'B': 2, 'C': 1};
    if (prestigeRanks[championship.prestige] < 2 || !holder) {
        return null;
    }

    // Vacate championship(s), end current reign
    const championshipsSet = companyChampionships.filter(c => checkAreChampionshipsEqual(c, championship));
    for (const c of championshipsSet) {
        c.vacated = true;
        const champion = await getChampionshipSuperstar(c);
        await removeSuperstarChampionships(champion, [c]);

        // End current reign
        const oldReign = await getChampionshipCurrentReign(c);
        if (oldReign) {
            oldReign.endMonth = month;
            oldReign.endYear = year;
            oldReign.vacated = true;
        }

        // Check talent request
        let requestData = {'championshipId': c.$loki};
        await checkTalentRequestAction({company: company, requestType: 'vacate_championship', talent: champion,
            data: requestData});
    }

    // Create match contract at next event
    const matchContract = MatchContract();
    await MatchContracts.insert(matchContract);
    matchContract.name = 'Option C Title Match';
    matchContract.awarded = true;

    // Add match contract to next monthly event's parent event
    const monthlyParentEvent = await getUpcomingMonthlyEventForWeeklyEvent(currentEvent, parentEvents, company);
    await addEventMatchContracts(monthlyParentEvent, [matchContract]);

    // Add bonus
    const bonuses = {'C': 12, 'B': 25, 'A': 50};
    matchContract.bonus = bonuses[monthlyParentEvent.prestige] || 12;

    // Create match contract's championship(s)
    let numberOfChampionships = 1;
    if (championship.tagTeam) {
        numberOfChampionships = 2;
    } else if (championship.trio) {
        numberOfChampionships = 3;
    }
    const newChampionships = [];
    for (let i = 0; i < numberOfChampionships; i++) {
        const newChampionship = Championship(matchContract.name);
        await Championships.insert(newChampionship);
        newChampionship.matchContract = true;
        newChampionship.rank = 'Upper Midcarder';
        newChampionship.tagTeam = championship.tagTeam;
        newChampionship.trio = championship.trio;
        await addChampionshipMatchContracts(newChampionship, [matchContract]);
        await addSuperstarChampionships(holderSuperstars[i], [newChampionship]);
        newChampionships.push(newChampionship);
    }
    await addCompanyChampionships(company, newChampionships);

    // Add match contract to holder
    await addTalentMatchContracts(holder, [matchContract]);

    return newChampionships;
}

export {
    getUniqueChampionships, compareChampionships, getChampionshipMatchData, getMatchAvailableChampionships,
    getVacatedChampionshipsForMatch, checkAreChampionshipsEqual, getChampionshipHolder, getChampionshipBookingData,
    checkChampionshipGimmickRestriction, checkChampionshipSegmentItemsToUnlock, getChampionshipXpChange,
    getChampionshipContractHolders, getChampionshipsForCashin, addCashinFutureMatch, addCashinToMatch,
    runCustomizeChampionshipAction, runSplitChampionshipAction, runOptionCAction, unifyChampionships, getTagChampionshipsSets}